import React, { useCallback, useEffect, useMemo, useState } from "react";
import Watchlist from "./Watchlist";
import SymbolDetails from "./SymbolDetails";
import useOkxTickers from "../../hooks/useOkxTickers";
import { loadWatchlists, saveWatchlists, symbolsOf } from "../../lib/paper/paperStore";

// Right-side sidebar: live watchlist + symbol details, self-persisting.
// `selected` / `onSelect` let the parent sync the chart symbol.
// `onQuotes` bubbles the live quote map up so the workspace (ticket, dock,
// engine) can share one feed subscription.
export default function WatchlistSidebar({ selected, onSelect, onQuotes, markSymbols = [] }) {
  const [data, setData] = useState(() => loadWatchlists());
  const activeList = useMemo(
    () => data.lists.find((l) => l.id === data.activeId) || data.lists[0],
    [data]
  );
  const listSymbols = useMemo(() => symbolsOf(activeList), [activeList]);
  const markKey = markSymbols.join(",");
  const symbols = useMemo(
    () => [...new Set([...markSymbols, ...listSymbols])],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listSymbols, markKey]
  );
  const { quotes, connected } = useOkxTickers(symbols);

  useEffect(() => saveWatchlists(data), [data]);
  useEffect(() => {
    onQuotes?.(quotes);
  }, [quotes, onQuotes]);

  const mutateActive = useCallback(
    (fn) => {
      setData((d) => ({
        ...d,
        lists: d.lists.map((l) => (l.id === (d.activeId || d.lists[0].id) ? fn(l) : l)),
      }));
    },
    []
  );

  const addSymbol = useCallback(
    (base) =>
      mutateActive((l) => {
        const sections = l.sections.length ? l.sections : [{ id: "crypto", name: "CRYPTO", collapsed: false, symbols: [] }];
        // add to first section if not already present anywhere
        if (symbolsOf(l).includes(base)) return l;
        return { ...l, sections: sections.map((s, i) => (i === 0 ? { ...s, symbols: [...s.symbols, base] } : s)) };
      }),
    [mutateActive]
  );

  const removeSymbol = useCallback(
    (sectionId, base) =>
      mutateActive((l) => ({
        ...l,
        sections: l.sections.map((s) => (s.id === sectionId ? { ...s, symbols: s.symbols.filter((x) => x !== base) } : s)),
      })),
    [mutateActive]
  );

  const toggleSection = useCallback(
    (sectionId) =>
      mutateActive((l) => ({
        ...l,
        sections: l.sections.map((s) => (s.id === sectionId ? { ...s, collapsed: !s.collapsed } : s)),
      })),
    [mutateActive]
  );

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-[3] min-h-0">
          <Watchlist
            list={activeList}
            quotes={quotes}
            selected={selected}
            onSelect={onSelect}
            onAddSymbol={addSymbol}
            onRemoveSymbol={removeSymbol}
            onToggleSection={toggleSection}
            markSymbols={markSymbols}
          />
        </div>
        <div className="flex-[2] min-h-0 overflow-hidden">
          <SymbolDetails base={selected} quote={quotes[selected]} onOpenChart={onSelect} />
        </div>
      </div>
      <div className="flex items-center gap-1 px-2 py-1 text-[10px] text-zinc-500 border-t border-zinc-800">
        <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-400" : "bg-amber-400"}`} />
        OKX {connected ? "live" : "connecting…"}
      </div>
    </div>
  );
}
